/**
 * Shared check: direct member of ADMIN_GROUP only (same GitLab logic as catalog edit’s admin path).
 * Used for DevTools sidebar + /devtools and /api-docs route guards.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Progress } from '@backstage/core-components';
import { DevToolsAccessDeniedPage } from './DevToolsAccessDenied';
import { ApiDocsAccessDeniedPage } from './ApiDocsAccessDenied';
import { useApi } from '@backstage/core-plugin-api';
import { configApiRef, fetchApiRef } from '@backstage/core-plugin-api';

type Value = boolean | null;

const AdminGroupMemberContext = createContext<Value>(null);

export function AdminGroupMemberProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const [allowed, setAllowed] = useState<Value>(null);

  useEffect(() => {
    let cancelled = false;
    const backend = config.getString('backend.baseUrl').replace(/\/$/, '');
    const url = `${backend}/api/catalog-edit-check/admin-group-member`;
    fetchApi
      .fetch(url, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { allowed: false }))
      .then((b: { allowed?: boolean }) => {
        if (!cancelled) setAllowed(Boolean(b?.allowed));
      })
      .catch(() => {
        if (!cancelled) setAllowed(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchApi, config]);

  const v = useMemo(() => allowed, [allowed]);
  return (
    <AdminGroupMemberContext.Provider value={v}>{children}</AdminGroupMemberContext.Provider>
  );
}

export function useAdminGroupMember(): Value {
  return useContext(AdminGroupMemberContext);
}

/** Full page: only direct ADMIN_GROUP members; others redirect (e.g. API explorer, catalog graph). */
export function AdminOnlyRoute({
  children,
  redirectTo = '/catalog',
}: {
  children: ReactNode;
  redirectTo?: string;
}): JSX.Element {
  const admin = useAdminGroupMember();
  if (admin === null) {
    return <Progress />;
  }
  if (!admin) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}

/**
 * Block /devtools and /api-docs unless user is a direct ADMIN_GROUP member.
 * (Sidebar links are hidden for non-admins; this covers direct URLs.)
 */
export function DevToolsRouteGuard({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const location = useLocation();
  const allowed = useAdminGroupMember();
  const path = location.pathname;

  const isDevTools = path.startsWith('/devtools');
  const isApiDocs = path.startsWith('/api-docs');
  if (!isDevTools && !isApiDocs) {
    return <>{children}</>;
  }
  if (allowed === null) {
    return <Progress />;
  }
  if (!allowed) {
    return isDevTools ? <DevToolsAccessDeniedPage /> : <ApiDocsAccessDeniedPage />;
  }
  return <>{children}</>;
}
